/**
 * planner/timeline.ts — the living roadmap (pure functions).
 *
 * The timeline is COMPUTED at read time from recorded plans and goals, so
 * every milestone "automatically updates" without any stored schedule. It
 * buckets milestones into the seven spec horizons relative to `now`.
 */

import {
  TIMELINE_HORIZONS,
  type LifestylePlan,
  type PlannerGoal,
  type PlannerTimeline,
  type TimelineHorizon,
  type TimelineMilestone,
  type TravelPlanState,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

export function horizonFor(at: number, now: number): TimelineHorizon | null {
  const days = (at - now) / DAY;
  if (days < 0) return null; // the past is not a plan
  if (days < 1) return 'today';
  if (days < 7) return 'thisWeek';
  if (days < 31) return 'thisMonth';
  if (days < 62) return 'nextMonth';
  if (days < 93) return 'threeMonths';
  if (days < 186) return 'sixMonths';
  if (days < 372) return 'oneYear';
  return null; // beyond the one-year roadmap
}

function emptyTimeline(): PlannerTimeline {
  const t = {} as PlannerTimeline;
  for (const h of TIMELINE_HORIZONS) t[h] = [];
  return t;
}

export function computeTimeline(input: {
  travel: TravelPlanState;
  lifestyle: LifestylePlan;
  goals: PlannerGoal[];
  now: number;
}): PlannerTimeline {
  const { travel, lifestyle, goals, now } = input;
  const timeline = emptyTimeline();
  const place = (m: TimelineMilestone | null) => {
    if (m) timeline[m.horizon].push(m);
  };
  const milestone = (
    id: string,
    at: number,
    label: string,
    kind: TimelineMilestone['kind'],
  ): TimelineMilestone | null => {
    const horizon = horizonFor(at, now);
    return horizon ? { id, horizon, at, label, kind } : null;
  };

  const allDestinations = [
    ...(travel.nextDestination ? [travel.nextDestination] : []),
    ...travel.futureDestinations,
  ];
  for (const d of allDestinations) {
    if (d.plannedArrival != null) {
      place(milestone(`arrive-${d.id}`, d.plannedArrival, `Arrive in ${d.city}, ${d.country}`, 'travel'));
    }
    if (d.plannedDeparture != null) {
      place(milestone(`depart-${d.id}`, d.plannedDeparture, `Depart ${d.city}`, 'travel'));
    }
  }

  for (const g of goals) {
    if (g.completedAt != null) continue;
    if (g.targetDate != null) {
      place(milestone(
        `goal-${g.id}`,
        g.targetDate,
        `Goal due: ${g.title} (${Math.round((g.currentAmount / g.targetAmount) * 100)}%)`,
        'goal',
      ));
    }
  }

  // Stay duration → an end-of-stay lifestyle milestone from the recorded plan.
  if (lifestyle.stayDurationMonths != null && travel.currentBaseCity != null) {
    const at = now + lifestyle.stayDurationMonths * 30 * DAY;
    place(milestone(
      'stay-ends',
      at,
      `Planned ${travel.currentBaseCity} stay ends (~${lifestyle.stayDurationMonths} month${lifestyle.stayDurationMonths === 1 ? '' : 's'})`,
      'lifestyle',
    ));
  }

  for (const h of TIMELINE_HORIZONS) {
    timeline[h].sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
  }
  return timeline;
}
